/**
 * Pure narrative helper functions for transparency steps.
 *
 * Shared between client components (ReasoningTimeline) and other code.
 * No browser or React dependencies.
 */
import type {
  TransparencyStep,
  SourceKind,
} from '../types/transparency';

// Source registry subset — classifyBashCommand

/** A known data source pattern for bash command classification. */
interface SourceMatch {
  pattern: RegExp;
  kind: SourceKind;
  identifier: string;
  name: string;
}

/** Minimal source registry for client-side command classification. */
const SOURCE_PATTERNS: SourceMatch[] = [
  { pattern: /salesforce\/account/, kind: 'salesforce', identifier: 'account.json', name: 'Salesforce account' },
  { pattern: /salesforce\/contacts/, kind: 'salesforce', identifier: 'contacts.json', name: 'Salesforce contacts' },
  { pattern: /salesforce\/opportunities/, kind: 'salesforce', identifier: 'opportunities.json', name: 'Salesforce opportunities' },
  { pattern: /salesforce\/tasks/, kind: 'salesforce', identifier: 'sf_tasks', name: 'Salesforce tasks' },
  { pattern: /salesforce\/events/, kind: 'salesforce', identifier: 'sf_events', name: 'Salesforce events' },
  { pattern: /conversations\/emails\//, kind: 'salesforce', identifier: 'sf_emails', name: 'Email conversations' },
  { pattern: /conversations\/transcripts\//, kind: 'gong', identifier: 'sf_transcripts', name: 'Call transcripts' },
  { pattern: /gong_call\//, kind: 'gong', identifier: 'gong_call', name: 'Gong calls' },
  { pattern: /salesforce\//, kind: 'salesforce', identifier: 'sf_misc', name: 'Salesforce data' },
  { pattern: /conversations\//, kind: 'gong', identifier: 'conversations_misc', name: 'Conversation data' },
];

/** Classify a bash command against the source registry. */
function classifyBashCommand(command: string): SourceMatch | null {
  for (const entry of SOURCE_PATTERNS) {
    if (entry.pattern.test(command)) return entry;
  }
  return null;
}

// Step helpers

export function stepLabel(step: TransparencyStep): string {
  switch (step.type) {
    case 'retrieval':
      return `Scanned ${step.name}`;
    case 'tool':
      return `Queried ${classifyBashCommand(step.arguments)?.name ?? 'data source'}`;
    case 'generation':
      return step.name;
    case 'transform':
      return `Processed ${step.operation}`;
    default:
      return (step as TransparencyStep).name;
  }
}

// Presentation helpers

export function sourceKindForStep(step: TransparencyStep): SourceKind {
  switch (step.type) {
    case 'retrieval':
      return step.source.kind;
    case 'tool': {
      const match = classifyBashCommand(step.arguments);
      return match?.kind ?? 'database';
    }
    case 'generation':
      return 'database';
    case 'transform':
      return 'file';
    default:
      return 'file';
  }
}

export function sourceLabelForStep(step: TransparencyStep): string {
  switch (step.type) {
    case 'retrieval':
      return step.source.identifier;
    case 'tool': {
      const match = classifyBashCommand(step.arguments);
      return match?.identifier ?? step.toolName;
    }
    case 'generation':
      return step.model;
    case 'transform':
      return step.operation;
    default:
      return (step as TransparencyStep).name;
  }
}

export function isSynthesisStep(step: TransparencyStep): boolean {
  return step.type === 'generation' && step.name.toLowerCase().includes('synthesis');
}

export function kindColor(step: TransparencyStep): string {
  if (isSynthesisStep(step)) return 'border-pink-500 bg-pink-500/20 dark:border-pink-400 dark:bg-pink-400/20';
  switch (step.type) {
    case 'retrieval':
      return 'border-blue-500 bg-blue-500/20 dark:border-blue-400 dark:bg-blue-400/20';
    case 'tool':
      return 'border-amber-500 bg-amber-500/20 dark:border-amber-400 dark:bg-amber-400/20';
    case 'generation':
      return 'border-violet-500 bg-violet-500/20 dark:border-violet-400 dark:bg-violet-400/20';
    case 'transform':
      return 'border-emerald-500 bg-emerald-500/20 dark:border-emerald-400 dark:bg-emerald-400/20';
    default:
      return 'border-gray-400 bg-gray-400/20 dark:border-nightshade-500 dark:bg-nightshade-500/20';
  }
}

export function stepResultSummary(step: TransparencyStep): string {
  switch (step.type) {
    case 'retrieval': {
      const s = step;
      if (s.resultCount != null) {
        return `${s.resultCount} record${s.resultCount === 1 ? '' : 's'} returned`;
      }
      return s.resultPreview ?? 'Data retrieved';
    }
    case 'tool': {
      const s = step;
      if (s.resultRows != null) {
        return `${s.resultRows} row${s.resultRows === 1 ? '' : 's'} returned`;
      }
      return s.result ? s.result.slice(0, 80) + (s.result.length > 80 ? '...' : '') : 'Completed';
    }
    case 'generation': {
      const s = step;
      return `${s.inputTokens + s.outputTokens} tokens (${s.model})`;
    }
    case 'transform': {
      const s = step;
      return s.outputDescription;
    }
    default:
      return `${(step as TransparencyStep).durationMs}ms`;
  }
}
